import Cookies from "js-cookie";
import React, {
  createContext,
  useContext,
  useRef,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { useUserPermissions } from "../../../../UserPermissionsContext";

interface CallContextType {
  isCalling: boolean;
  teacherName?: string;
  callTimer: number;
  startCall: (teacherId: number, teacherName: string) => Promise<void>;
  endCall: () => void;
}

const SedCallContext = createContext<CallContextType | undefined>(undefined);

interface CallProviderProps {
  children: ReactNode;
}

export const SedCallProvider: React.FC<CallProviderProps> = ({ children }) => {
  const { userPermissions, webApi } = useUserPermissions();

  const jwtToken = Cookies.get("authToken");
  const [isCalling, setIsCalling] = useState(false);
  const [teacherName, setTeacherName] = useState("");
  const [callTimer, setCallTimer] = useState(0);
  const [correlationId, setCorrelationId] = useState("");

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const startTimer = () => {
    let seconds = 0;

    timerRef.current = setInterval(() => {
      seconds++;
      setCallTimer(seconds);
    }, 1000);
  };

  const options = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${jwtToken}`,
    },
  };

  const changeAgentStatus = async (statusId: number) => {
    try {
      const res = await fetch(
        `${webApi}/user_monitoring/change_agent_status/${statusId}/`,
        {
          method: "POST", // change to POST if backend expects POST
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
          },
          body: JSON.stringify({}),
        },
      );

      const data = await res.json();
      console.log("Agent status changed:", data);
    } catch (error) {
      console.error("Status change failed:", error);
    }
  };

  const endCall = async () => {
    setIsCalling(false);
    setCallTimer(0);
    setTeacherName("");
    setCorrelationId("");
    if (timerRef.current) clearInterval(timerRef.current);
    if (pollingRef.current) clearInterval(pollingRef.current);
    // Agent status -> Available
    await changeAgentStatus(1);
  };

  const startPolling = (corrId: string) => {
    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch(
          `${webApi}call/is_call_end/?correlation_id=${corrId}`,options
        );

        const data = await res.json();

        console.log("Polling:", data);

        if (data.is_call_end === true && isCalling) {
          endCall();
        }

      } catch (error) {
        console.error(error);
      }
    }, 1000);
  };

  //   Start Call
  const startCall = async (teacherId: number, name: string) => {
    try {
      const res = await fetch(
        `${webApi}/call/sed_teacher_calling/?teacher_id=${teacherId}`,
        options,
      );

      const data = await res.json();

      if (data.status === "success") {
        setTeacherName(name);
        setCorrelationId(data.correlationId);
        setIsCalling(true);

        // Agent status -> On Call
        await changeAgentStatus(3);

        startTimer();
        startPolling(data.correlationId);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  return (
    <SedCallContext.Provider
      value={{
        isCalling,
        teacherName,
        callTimer,
        startCall,
        endCall,
      }}
    >
      {children}
    </SedCallContext.Provider>
  );
};

export const useSEDCallContext = () => {
  const context = useContext(SedCallContext);
  if (!context) {
    throw new Error("useCallContext must be used within CallProvider");
  }
  return context;
};

const CallPopup = () => {
  const { isCalling, teacherName, callTimer, endCall } = useSEDCallContext();

  if (!isCalling) return null;

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  return (
    <div
      className="position-fixed top-0 end-0 m-3 bg-white shadow rounded p-3 d-flex align-items-center"
      style={{ zIndex: 9999 }}
    >
      <div className="me-3">
        📞 Calling to {teacherName} - {formatTime(callTimer)}
      </div>

      <button className="btn btn-danger btn-sm" onClick={endCall}>
        End Call
      </button>
    </div>
  );
};

export default CallPopup;
